// Stock API 서비스
import { API_BASE_URL, getToken } from './api';

const STOCK_API_BASE = `${API_BASE_URL}/api/stock`;

// 인증 헤더 가져오기
const getAuthHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// 주식 목록 조회
export const getStockList = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.market) queryParams.append('market', params.market);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.changeType) queryParams.append('changeType', params.changeType);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetch(`${STOCK_API_BASE}/list?${queryParams.toString()}`);
    return await response.json();
  } catch (error) {
    console.error('주식 목록 조회 실패:', error);
    throw error;
  }
};

// 종목 상세 조회
export const getStockDetail = async (symbol) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/detail/${symbol}`);
    return await response.json();
  } catch (error) {
    console.error('종목 상세 조회 실패:', error);
    throw error;
  }
};

// 호가 데이터 조회
export const getOrderbook = async (symbol) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/orderbook/${symbol}`);
    return await response.json();
  } catch (error) {
    console.error('호가 데이터 조회 실패:', error);
    throw error;
  }
};

// 관심종목 목록 조회
export const getWatchlist = async () => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/watchlist`, {
      headers: getAuthHeaders()
    });
    return await response.json();
  } catch (error) {
    console.error('관심종목 조회 실패:', error);
    throw error;
  }
};

// 관심종목 추가
export const addToWatchlist = async (symbol) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/watchlist/${symbol}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    });
    return await response.json();
  } catch (error) {
    console.error('관심종목 추가 실패:', error);
    throw error;
  }
};

// 관심종목 삭제
export const removeFromWatchlist = async (symbol) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/watchlist/${symbol}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return await response.json();
  } catch (error) {
    console.error('관심종목 삭제 실패:', error);
    throw error;
  }
};

// 여러 관심종목 일괄 삭제
export const removeMultipleFromWatchlist = async (symbols) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/watchlist`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ symbols })
    });
    return await response.json();
  } catch (error) {
    console.error('관심종목 일괄 삭제 실패:', error);
    throw error;
  }
};

// 관심종목 여부 확인
export const checkWatchlist = async (symbol) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/watchlist/check/${symbol}`, {
      headers: getAuthHeaders()
    });
    return await response.json();
  } catch (error) {
    console.error('관심종목 확인 실패:', error);
    throw error;
  }
};

// 종목 검색
export const searchStocks = async (query) => {
  try {
    if (!query || query.length < 1) {
      return { success: true, data: [] };
    }
    const response = await fetch(`${STOCK_API_BASE}/search?q=${encodeURIComponent(query)}`);
    return await response.json();
  } catch (error) {
    console.error('종목 검색 실패:', error);
    throw error;
  }
};

// 지원 종목 목록
export const getSupportedStocks = async () => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/supported`);
    return await response.json();
  } catch (error) {
    console.error('지원 종목 조회 실패:', error);
    throw error;
  }
};

// 데이터 소스 정보
export const getDataSource = async () => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/source`);
    return await response.json();
  } catch (error) {
    console.error('데이터 소스 조회 실패:', error);
    throw error;
  }
};

// 차트 데이터 조회
export const getChartData = async (symbol, period = '3mo') => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/chart/${symbol}?period=${period}`);
    return await response.json();
  } catch (error) {
    console.error('차트 데이터 조회 실패:', error);
    throw error;
  }
};

// 종목 뉴스 조회
export const getStockNews = async (symbol, limit = 20) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/news/${symbol}?limit=${limit}`);
    return await response.json();
  } catch (error) {
    console.error('뉴스 조회 실패:', error);
    throw error;
  }
};

// 기술적 지표 조회
export const getIndicators = async (symbol, period = '3mo', indicators = []) => {
  try {
    const indicatorsParam = indicators.length > 0 ? indicators.join(',') : '';
    const response = await fetch(
      `${STOCK_API_BASE}/indicators/${symbol}?period=${period}&indicators=${indicatorsParam}`
    );
    return await response.json();
  } catch (error) {
    console.error('지표 조회 실패:', error);
    throw error;
  }
};

// AI 종합 분석 조회
export const getAISummary = async (symbol) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/ai-summary/${symbol}`);
    return await response.json();
  } catch (error) {
    console.error('AI 분석 조회 실패:', error);
    throw error;
  }
};

// ===========================================
// 유틸리티 함수
// ===========================================

// 숫자 포맷 (한국식)
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  if (num >= 100000000) return (num / 100000000).toFixed(1) + '억';
  if (num >= 10000) return (num / 10000).toFixed(1) + '만';
  return num.toLocaleString();
};

// 가격 포맷
export const formatPrice = (price) => {
  if (price === null || price === undefined) return '-';
  return price.toLocaleString() + '원';
};

// 변동률 포맷
export const formatChange = (change) => {
  if (change === null || change === undefined) return '0.00%';
  const sign = change > 0 ? '+' : '';
  return sign + change.toFixed(2) + '%';
};

// 변동 금액 포맷
export const formatChangeAmount = (amount) => {
  if (amount === null || amount === undefined) return '-';
  const sign = amount > 0 ? '+' : '';
  return sign + amount.toLocaleString() + '원';
};

// DART 공시 정보 조회
export const getDisclosures = async (symbol, limit = 20) => {
  try {
    const response = await fetch(`${STOCK_API_BASE}/disclosures/${symbol}?limit=${limit}`);
    return await response.json();
  } catch (error) {
    console.error('공시 정보 조회 실패:', error);
    throw error;
  }
};
